import { useState, useEffect } from 'react';

// Key for the list of named/saved builds
const STORAGE_KEY = 'anr_saved_configs';
// Key for the auto-save (refresh persistence)
const DRAFT_KEY = 'anr_current_draft';

export default function useConfigShare() {
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Load saved configs on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        setSavedConfigs(JSON.parse(saved));
      }
    } catch (e) {
      console.error("Failed to load saved configs", e);
    }
  }, []);

  // 1. Get Config from URL
  const getConfigFromURL = () => {
    const params = new URLSearchParams(window.location.search);
    const configData = params.get('config');
    if (configData) {
      try {
        return JSON.parse(atob(configData));
      } catch (e) {
        console.error("Error parsing URL config", e);
      }
    }
    return null;
  };

  // 2. Generate Shareable URL
  const generateShareableURL = (config) => {
    if (!config) return window.location.href;
    const jsonString = JSON.stringify(config);
    const base64 = btoa(jsonString);
    return `${window.location.origin}${window.location.pathname}?config=${base64}`;
  };

  // 3. Save "Current" to LocalStorage (Auto-save for page refresh)
  // Every localStorage call below is guarded. An unguarded one throws during
  // mount or on a state change, which unmounts the whole builder and leaves the
  // customer with a blank page that repeats on every visit. Causes seen in the
  // wild: a truncated draft value (JSON.parse throws), and storage blocked or
  // full (setItem throws) under Safari "Block all cookies" and some content
  // blockers. Losing a draft is fine; taking the builder down is not.
  const saveToLocalStorage = (config) => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(config));
    } catch (e) {
      console.error("Failed to auto-save draft", e);
    }
  };

  const loadFromLocalStorage = () => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      console.error("Failed to load draft - discarding it", e);
      // Drop the bad value so it cannot break the next visit too.
      try { localStorage.removeItem(DRAFT_KEY); } catch (err) { /* blocked */ }
      return null;
    }
  };

  // 4. Save Named Config (FIXED ARGUMENT ORDER)
  // Previous versions might have been (name, config). 
  // It MUST be (config, name) to match ShareModal.js logic.
  const saveNamedConfig = (config, name) => {
    const newSave = {
      id: Date.now(),
      name: name,     // Name string
      config: config, // Config object
      createdAt: Date.now(),
    };

    const updated = [newSave, ...savedConfigs];
    setSavedConfigs(updated);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    } catch (e) {
      console.error("Failed to save named config", e);
    }
  };

  // 5. Delete Config
  const deleteNamedConfig = (id) => {
    const updated = savedConfigs.filter((c) => c.id !== id);
    setSavedConfigs(updated);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    } catch (e) {
      console.error("Failed to delete named config", e);
    }
  };

  // 6. Copy to Clipboard
  const copyToClipboard = (text) => {
    const flagSuccess = () => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    };

    // Fallback for contexts where the async Clipboard API is unavailable or
    // rejects (non-HTTPS, embedded iframe, permission denied). Previously this
    // promise had no .catch(), so a failed copy did nothing at all.
    const legacyCopy = () => {
      try {
        const el = document.createElement('textarea');
        el.value = text;
        el.setAttribute('readonly', '');
        el.style.position = 'fixed';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
        flagSuccess();
      } catch (e) {
        console.error('Copy to clipboard failed', e);
      }
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(flagSuccess).catch(legacyCopy);
    } else {
      legacyCopy();
    }
  };

  return {
    getConfigFromURL,
    generateShareableURL,
    saveToLocalStorage,
    loadFromLocalStorage,
    savedConfigs,
    saveNamedConfig,
    deleteNamedConfig,
    copyToClipboard,
    copySuccess,
    shareModalOpen,
    setShareModalOpen,
  };
}